import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { HashLink } from "react-router-hash-link";
import HomeIcon from "@mui/icons-material/Home";
import React from "react";
import { Helmet } from "react-helmet-async";

const VisitUs = () => {
  return (
    <>
    <Helmet>
  <title>Visit Den Den Claw | Arcade Hours & Location in Ottawa</title>
  <meta
    name="description"
    content="Find Den Den Claw in Ottawa’s Chinatown. See our hours, location, and what to expect when visiting our claw machine arcade."
  />
  <link rel="canonical" href="https://www.dendenclaw.com/contact" />
</Helmet>

    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        {/* ---------- HERO ---------- */}
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            fontWeight: 800,
            color: "primary.main",
            mb: 1,
          }}
        >
          Come Visit Den Den Claw 🎮
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            mb: 6,
          }}
        >
           Located in Ottawa’s Chinatown, Den Den Claw is a walk-in friendly arcade
  open to all ages. ✨
        </Typography>

        {/* ---------- LOCATION + HOURS ---------- */}
<Card
  sx={{
    borderRadius: 3,
    boxShadow: 6,
    p: { xs: 3, sm: 4 },
    mb: 6,
    background: "linear-gradient(180deg, #ffffff 0%, #f5faff 100%)",
  }}
>
  <CardContent sx={{ px: 0 }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={4}
      alignItems="stretch"
    >
      {/* LOCATION */}
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          mb={1}
        >
          <LocationOnIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Location
          </Typography>
        </Stack>

        <Typography sx={{ mb: 2 }}>
          691 Somerset St W
          <br />
          Ottawa, ON K1R 6P5
        </Typography>

        <Button
          size="small"
          variant="outlined"
          href="https://maps.app.goo.gl/divxnw3iNqiqT3ru8"
          target="_blank"
          sx={{ borderRadius: 999 }}
        >
          Open in Maps
        </Button>
      </Box>

      {/* DESKTOP-ONLY DIVIDER */}
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          width: "1px",
          background:
            "linear-gradient(180deg, transparent, #cfe8ff, transparent)",
        }}
      />

      {/* HOURS */}
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          mb={1}
        >
          <AccessTimeIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Hours
          </Typography>
        </Stack>

        <Typography>
          Mon: 3:30PM – 9:00PM
          <br />
          Tue: Closed
          <br />
          Wed–Thu: 3:30PM – 9:00PM
          <br />
          Fri: 3:30PM – 10:00PM
          <br />
          Sat: 12:00PM – 10:00PM
          <br />
          Sun: 12:00PM – 9:00PM
        </Typography>
      </Box>
    </Stack>
  </CardContent>
</Card>

        {/* ---------- FIRST TIME HERE ---------- */}
<Box
  sx={{
    mb: 4,
    p: 3,
    borderRadius: 3,
    boxShadow: 4,
    textAlign: "center",
  }}
>
  <Typography
    variant="h6"
    sx={{ fontWeight: 700, mb: 1 }}
  >
    First time here? 👋
  </Typography>

  <Typography
    sx={{
      color: "text.secondary",
      maxWidth: 520,
      mx: "auto",
      mb: 2,
    }}
  >
    Wondering how claw machines work, how points work, or what to expect
    when you visit?
  </Typography>

  <Button
    component={HashLink}
    smooth
    to="/about#about"
    variant="outlined"
    sx={{
      borderRadius: 999,
      px: 4,
      textTransform: "none",
      fontWeight: 600,
    }}
  >
    Read the FAQ →
  </Button>
</Box>


        {/* ---------- WHAT TO EXPECT ---------- */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 6,
            mb: 6,
            background: "linear-gradient(180deg, #f5faff 0%, #ffffff 100%)",
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={2}>
              What to Expect ✨
            </Typography>

            <Typography sx={{ mb: 1 }}>
              🎯 A store full of claw machines — from beginner-friendly to
              skill-based Japanese UFO catchers
            </Typography>

            <Typography sx={{ mb: 1 }}>
              🧸 Win prizes and keep them, or trade them in for points toward
              something bigger
            </Typography>

            <Typography sx={{ mb: 1 }}>
              🎨 Artist Alley featuring local artists and handmade goods
            </Typography>
            <Typography sx={{ mb: 1 }}>
              📸 Cute Japanese photobooth with filters and props
            </Typography>
            <Typography>
              💙 Friendly staff who actually help you learn how to win
            </Typography>
          </CardContent>
        </Card>
        

        {/* ---------- CONTACT CTA ---------- */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 6,
            mb: 6,
            textAlign: "center",
            background: "linear-gradient(180deg, #e8f2ff 0%, #f5faff 100%)",
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Questions before you come? 💬
            </Typography>

            <Typography sx={{ mb: 3, color: "text.secondary" }}>
              Text us, email us, or DM us — we’re happy to help.
            </Typography>

  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={2}
    justifyContent="center"
  >
    {/* EMAIL */}
    <Button
      variant="contained"
      startIcon={<EmailIcon />}
      href={
        "mailto:dendendori@gmail.com" +
        "?subject=Visiting%20Den%20Den%20Claw" +
        "&body=Hi%20Den%20Den%20Claw!%0A%0AI%20have%20a%20question%20about%20visiting.%0A%0AThanks!"
      }
      sx={{
        borderRadius: 999,
        px: 3,
        textTransform: "none",
        fontWeight: 600,
      }}
    >
      dendendori@gmail.com
    </Button>

    {/* TEXT */}
    <Button
      variant="outlined"
      startIcon={<PhoneIphoneIcon />}
      href="tel:16134002909"
      sx={{
        borderRadius: 999,
        px: 3,
        textTransform: "none",
        fontWeight: 600,
      }}
    >
      Text (613) 400-2909
    </Button>

    {/* INSTAGRAM */}
    <Button
      variant="outlined"
      startIcon={<InstagramIcon />}
      href="https://instagram.com/dendenclaw"
      target="_blank"
      sx={{
        borderRadius: 999,
        px: 3,
        textTransform: "none",
        fontWeight: 600,
      }}
    >
      DM @dendenclaw
    </Button>
  </Stack>
          </CardContent>
        </Card>

        {/* ---------- MAP ---------- */}
        <Box
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: 6,
          }}
        >
          <iframe
            title="Den Den Claw Map"
            src="https://www.google.com/maps?q=691+Somerset+St+W,+Ottawa,+ON&output=embed"
            width="100%"
            height="350"
            style={{ border: 0 }}
            loading="lazy"
          />
        </Box>
          <Box
            sx={{
              mt: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >

          <HashLink smooth to="/#home" className="text-style">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              sx={{
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Back to Home
            </Button>
          </HashLink>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default VisitUs;
